import cn from 'classnames';
import { pluralize } from 'common/StringUtils';
import React, { useRef } from 'react';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';

interface FlexServingsQuantityPickerProps {
  quantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  disabled?: boolean;
  className?: string;
}

export const FlexServingsQuantityPicker: React.FC<FlexServingsQuantityPickerProps> = ({
  quantity,
  onIncrement,
  onDecrement,
  disabled = false,
  className,
}) => {
  const inCart = quantity > 0;
  const decrementIconName = quantity === 1 ? 'trash' : 'minusHandWritten';

  const lastServingCountRef = useRef(quantity);
  if (inCart) {
    lastServingCountRef.current = quantity;
  }
  const displayQuantity = lastServingCountRef.current;

  return (
    <div className={cn('relative flex h-[30px] w-full items-center justify-end', className)}>
      <div
        aria-hidden
        className={cn(
          'absolute inset-y-0 right-0 rounded ease-in-out',
          inCart
            ? 'w-full bg-raspberry transition-[width] duration-150'
            : 'w-8 bg-transparent transition-all delay-100 duration-150'
        )}
      />

      <button
        type='button'
        aria-label='Remove a serving'
        onClick={(e) => {
          e.stopPropagation();
          onDecrement();
        }}
        className={cn(
          'absolute inset-y-0 left-0 z-10 flex w-8 items-center justify-center transition-opacity duration-100',
          inCart ? 'opacity-100 delay-150' : 'pointer-events-none opacity-0'
        )}>
        <Icon name={decrementIconName} color={IconColor.WHITE} size={16} />
      </button>

      <span
        className={cn(
          'pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-100',
          'text-caption-600 leading-none tracking-[-0.28px] text-white',
          inCart ? 'opacity-100 delay-150' : 'opacity-0'
        )}>
        {displayQuantity} {pluralize('serving', displayQuantity)}
      </span>

      <button
        type='button'
        aria-label='Add a serving'
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onIncrement();
        }}
        className={cn(
          'relative z-10 flex w-8 shrink-0 items-center justify-center',
          inCart ? 'text-white' : 'text-raspberry transition-colors delay-100 duration-150'
        )}>
        <Icon name='plusHandWritten' size={inCart ? 16 : 24} />
      </button>
    </div>
  );
};
